import { useState, useEffect, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  LineChart, Wallet, Package, Receipt, Users, Target, Plus, Trash2, ListChecks,
  Loader2, Check, Save, Sparkles, Copy,
} from "lucide-react";

const OFFER_TYPES = ["product", "service", "resource"];
const OFFER_STATUSES = ["active", "planned", "inactive"];
const EXPENSE_RECURRENCE = ["recurring", "one-time"];

const STATUS_TONE = {
  active: "bg-primary text-primary-foreground",
  planned: "bg-accent/20 text-secondary",
  inactive: "bg-muted text-muted-foreground",
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) =>
  "$" + num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Live-derived totals so the founder never enters the same number twice.
const sumExpenses = (d) => (d?.expense_items || []).reduce((a, e) => a + num(e.amount), 0);
const sumOfferRevenue = (d) => (d?.offers || []).reduce((a, o) => a + num(o.revenue), 0);
const hasOfferRevenue = (d) => (d?.offers || []).some((o) => num(o.revenue) > 0);
const computeRevenue = (d) => (hasOfferRevenue(d) ? sumOfferRevenue(d) : num(d?.revenue));
const buildSaveDoc = (d) => ({ ...d, revenue: computeRevenue(d), expenses: sumExpenses(d) });
const currentMonthKey = () => new Date().toISOString().slice(0, 7);
const humanMonth = (m) => {
  if (!m) return "";
  const d = new Date(m + "-01T00:00:00");
  if (isNaN(d)) return m;
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const emptyDoc = (month) => ({
  month,
  revenue: 0,
  expenses: 0,
  revenue_goal: 0,
  offers: [],
  expense_items: [],
  new_leads: 0,
  new_customers: 0,
  activity_note: "",
  priorities: ["", "", "", "", ""],
});

const SectionCard = ({ icon: Icon, title, hint, action, children, testid }) => (
  <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid={testid}>
    <div className="flex items-start gap-3 mb-5">
      <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5 text-primary" strokeWidth={1.75} />
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="font-heading text-xl">{title}</h2>
        {hint && <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{hint}</p>}
      </div>
      {action}
    </div>
    {children}
  </Card>
);

const StatTile = ({ label, value, sub, testid, accent }) => (
  <div className="rounded-2xl bg-muted/30 border border-border px-4 py-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`font-heading text-2xl mt-1 ${accent || "text-foreground"}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

export default function BusinessCenter() {
  const [month, setMonth] = useState(currentMonthKey());
  const [doc, setDoc] = useState(null);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMonths = useCallback(async () => {
    try {
      const r = await api.get("/business/months");
      setMonths(r.data?.months || []);
    } catch (e) { /* non-fatal */ }
  }, []);

  const loadMonth = useCallback(async (m) => {
    setLoading(true);
    try {
      const r = await api.get(`/business/month/${m}`);
      const d = r.data || emptyDoc(m);
      if (!Array.isArray(d.priorities)) d.priorities = ["", "", "", "", ""];
      while (d.priorities.length < 5) d.priorities.push("");
      d.offers = d.offers || [];
      d.expense_items = d.expense_items || [];
      setDoc(d);
    } catch (e) {
      toast.error("Could not load this month.");
      setDoc(emptyDoc(m));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMonths(); }, [loadMonths]);
  useEffect(() => { loadMonth(month); }, [month, loadMonth]);

  const [saveState, setSaveState] = useState("idle");

  const persist = async (next) => {
    const payload = buildSaveDoc(next);
    await api.put(`/business/month/${payload.month}`, payload);
    loadMonths();
  };

  const setField = (key, value) => setDoc((d) => ({ ...d, [key]: value }));
  const save = () => { if (doc) persist(doc).catch(() => {}); };
  const saveNow = (next) => { setDoc(next); persist(next).catch(() => toast.error("Could not save. Please try again.")); };

  const saveMonth = async () => {
    if (!doc) return;
    setSaveState("saving");
    try {
      await persist(doc);
      const r = await api.get(`/business/month/${doc.month}`);
      const d = r.data || doc;
      if (!Array.isArray(d.priorities)) d.priorities = ["", "", "", "", ""];
      while (d.priorities.length < 5) d.priorities.push("");
      d.offers = d.offers || [];
      d.expense_items = d.expense_items || [];
      setDoc(d);
      setSaveState("saved");
      toast.success("Saved");
      setTimeout(() => setSaveState((s) => (s === "saved" ? "idle" : s)), 2500);
    } catch (e) {
      setSaveState("error");
      toast.error("Could not save. Please try again.");
    }
  };

  const setPriority = (idx, value) =>
    setDoc((d) => {
      const priorities = [...(d.priorities || [])];
      priorities[idx] = value;
      return { ...d, priorities };
    });

  const copyPriorities = async () => {
    try {
      const r = await api.get(`/business/month/${month}/prior-priorities`);
      const prev = r.data?.priorities || [];
      if (!prev.some((p) => (p || "").trim())) {
        toast.info("No previous priorities to copy yet.");
        return;
      }
      const priorities = [...(doc.priorities || ["", "", "", "", ""])];
      for (let i = 0; i < 5; i++) {
        if (!(priorities[i] || "").trim() && (prev[i] || "").trim()) priorities[i] = prev[i];
      }
      saveNow({ ...doc, priorities });
      toast.success("Copied last month's priorities.");
    } catch {
      toast.error("Could not copy priorities.");
    }
  };

  // Offers
  const addOffer = () => {
    const next = { ...doc, offers: [...(doc.offers || []), { id: crypto.randomUUID(), name: "", type: "product", price: 0, status: "planned", sales_count: 0, revenue: 0 }] };
    saveNow(next);
  };
  const updateOffer = (id, key, value) =>
    setDoc((d) => ({ ...d, offers: d.offers.map((o) => (o.id === id ? { ...o, [key]: value } : o)) }));
  const removeOffer = (id) => saveNow({ ...doc, offers: doc.offers.filter((o) => o.id !== id) });

  // Expenses
  const addExpense = () => {
    const next = { ...doc, expense_items: [...(doc.expense_items || []), { id: crypto.randomUUID(), name: "", amount: 0, category: "", recurrence: "one-time", active: true }] };
    saveNow(next);
  };
  const updateExpense = (id, key, value) =>
    setDoc((d) => ({ ...d, expense_items: d.expense_items.map((e) => (e.id === id ? { ...e, [key]: value } : e)) }));
  const removeExpense = (id) => saveNow({ ...doc, expense_items: doc.expense_items.filter((e) => e.id !== id) });

  const derived = useMemo(() => {
    if (!doc) return { revenue: 0, expenses: 0, net: 0, goal: 0, pct: 0, activeOffers: 0, fromOffers: false };
    const expenses = sumExpenses(doc);
    const revenue = computeRevenue(doc);
    const net = revenue - expenses;
    const goal = num(doc.revenue_goal);
    const pct = goal > 0 ? Math.round((revenue / goal) * 100) : 0;
    const activeOffers = (doc.offers || []).filter((o) => o.status === "active").length;
    return { revenue, expenses, net, goal, pct, activeOffers, fromOffers: hasOfferRevenue(doc) };
  }, [doc]);

  const activePriorities = useMemo(
    () => (doc?.priorities || []).filter((p) => (p || "").trim()),
    [doc]
  );

  if (loading || !doc) {
    return (
      <div>
        <PageHeader testid="business-header" title="Business Center" subtitle="A calm, honest picture of your business, the numbers you choose to track, nothing you don't." />
        <Card className="rounded-3xl border-border shadow-soft p-12 text-center text-muted-foreground" data-testid="business-loading">Gathering your numbers…</Card>
      </div>
    );
  }

  return (
    <div data-testid="business-page">
      <PageHeader
        testid="business-header"
        title="Business Center"
        subtitle="A calm, honest picture of your business, the numbers you choose to track, nothing you don't."
        action={
          <div className="flex items-end gap-3">
            <div className="flex flex-col items-start gap-1">
              <Label className="text-xs text-muted-foreground">Month</Label>
              <Input
                type="month"
                data-testid="month-selector"
                value={month}
                onChange={(e) => setMonth(e.target.value || currentMonthKey())}
                className="rounded-xl w-[160px]"
              />
            </div>
            <Button
              data-testid="save-month-btn"
              onClick={saveMonth}
              disabled={saveState === "saving"}
              className="rounded-full min-w-[132px]"
            >
              {saveState === "saving" ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</>
              ) : saveState === "saved" ? (
                <><Check className="h-4 w-4 mr-2" /> Saved</>
              ) : (
                <><Save className="h-4 w-4 mr-2" /> Save Month</>
              )}
            </Button>
          </div>
        }
      />

      {months.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="month-chips">
          {months.map((m) => (
            <button
              key={m}
              data-testid={`month-chip-${m}`}
              onClick={() => setMonth(m)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${m === month ? "bg-primary text-primary-foreground border-primary" : "bg-background text-foreground/70 border-border hover:bg-muted"}`}
            >
              {humanMonth(m)}
            </button>
          ))}
        </div>
      )}

      {doc.carried_from && (
        <div className="mb-6 rounded-2xl border border-primary/20 bg-primary/5 px-4 py-3 flex items-start gap-3" data-testid="carried-forward-note">
          <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" strokeWidth={1.75} />
          <p className="text-sm text-foreground/80">
            Your active offers, recurring expenses and revenue goal were carried over from{" "}
            <span className="font-medium">{humanMonth(doc.carried_from)}</span>. This month's revenue, leads
            and customers start fresh. Save the month to keep it.
          </p>
        </div>
      )}

      {/* Dashboard summary */}
      <Card className="rounded-3xl border-border shadow-soft p-6 sm:p-8 mb-6" data-testid="business-dashboard">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <LineChart className="h-5 w-5 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <h2 className="font-heading text-xl">{humanMonth(month)} at a glance</h2>
            <p className="text-sm text-muted-foreground mt-1">A quick summary of the month you're viewing.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile testid="stat-revenue" label="Revenue" value={money(derived.revenue)} />
          <StatTile testid="stat-expenses" label="Expenses" value={money(derived.expenses)} />
          <StatTile testid="stat-net" label="Net / Profit" value={money(derived.net)} accent={derived.net >= 0 ? "text-primary" : "text-destructive"} />
          <StatTile testid="stat-goal" label="Revenue goal" value={derived.goal > 0 ? `${derived.pct}%` : "—"} sub={derived.goal > 0 ? `of ${money(derived.goal)}` : "no goal set"} />
          <StatTile testid="stat-active-offers" label="Active offers" value={derived.activeOffers} />
          <StatTile testid="stat-leads" label="New leads" value={num(doc.new_leads)} />
          <StatTile testid="stat-customers" label="New customers" value={num(doc.new_customers)} />
          <StatTile testid="stat-priorities" label="Priorities" value={activePriorities.length} sub="in focus" />
        </div>
        {derived.goal > 0 && (
          <div className="mt-5">
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden" data-testid="goal-progress-bar">
              <div className="h-full bg-primary rounded-full transition-all duration-500" style={{ width: `${Math.min(derived.pct, 100)}%` }} />
            </div>
          </div>
        )}
        {activePriorities.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="dashboard-priorities">
            {activePriorities.map((p, i) => (
              <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-secondary/10 text-secondary">{p}</span>
            ))}
          </div>
        )}
      </Card>

      {/* Monthly Snapshot */}
      <SectionCard icon={Wallet} title="Monthly Snapshot" hint="Your headline numbers for the month. Revenue, expenses, net and goal progress calculate automatically from the data below." testid="section-snapshot">
        <div className="grid sm:grid-cols-3 gap-5">
          <div className="space-y-2">
            <Label>Total revenue ($)</Label>
            {derived.fromOffers ? (
              <div className="rounded-xl border border-border bg-muted/30 px-3 h-10 flex items-center font-medium" data-testid="snapshot-revenue">
                {money(derived.revenue)}
              </div>
            ) : (
              <Input type="number" step="0.01" min="0" data-testid="snapshot-revenue-input" value={doc.revenue}
                onChange={(e) => setField("revenue", e.target.value)} onBlur={save} className="rounded-xl" />
            )}
            <p className="text-xs text-muted-foreground">
              {derived.fromOffers ? "Summed automatically from offer revenue below." : "Add revenue to your offers below to sum it automatically, or enter a total here."}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Total expenses ($)</Label>
            <div className="rounded-xl border border-border bg-muted/30 px-3 h-10 flex items-center font-medium" data-testid="snapshot-expenses">
              {money(derived.expenses)}
            </div>
            <p className="text-xs text-muted-foreground">Summed automatically from the expense items below.</p>
          </div>
          <div className="space-y-2">
            <Label>Net / profit</Label>
            <div className={`rounded-xl border border-border bg-muted/30 px-3 h-10 flex items-center font-medium ${derived.net >= 0 ? "text-primary" : "text-destructive"}`} data-testid="snapshot-net">
              {money(derived.net)}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Monthly revenue goal ($)</Label>
            <Input type="number" step="0.01" min="0" data-testid="snapshot-goal" value={doc.revenue_goal}
              onChange={(e) => setField("revenue_goal", e.target.value)} onBlur={save} className="rounded-xl" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Progress toward goal</Label>
            <div className="rounded-xl border border-border bg-muted/30 px-3 h-10 flex items-center gap-3" data-testid="snapshot-progress">
              <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(derived.pct, 100)}%` }} />
              </div>
              <span className="text-sm text-muted-foreground shrink-0">{derived.goal > 0 ? `${derived.pct}%` : "no goal set"}</span>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Offers / Products / Services */}
      <SectionCard
        icon={Package}
        title="Offers, Products & Services"
        hint="What you offer, and how each one did this month."
        testid="section-offers"
        action={<Button size="sm" variant="outline" className="rounded-full" data-testid="add-offer-btn" onClick={addOffer}><Plus className="h-4 w-4 mr-1" /> Add</Button>}
      >
        {(doc.offers || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="offers-empty">No offers yet. Add your first product, service or resource.</p>
        ) : (
          <div className="space-y-3" data-testid="offers-list">
            {doc.offers.map((o) => (
              <div key={o.id} className="rounded-2xl border border-border p-4 bg-muted/20" data-testid={`offer-${o.id}`}>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Name</Label>
                    <Input data-testid={`offer-name-${o.id}`} value={o.name} placeholder="Offer name"
                      onChange={(e) => updateOffer(o.id, "name", e.target.value)} onBlur={save} className="rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Type</Label>
                      <Select value={o.type} onValueChange={(v) => saveNow({ ...doc, offers: doc.offers.map((x) => x.id === o.id ? { ...x, type: v } : x) })}>
                        <SelectTrigger className="rounded-xl" data-testid={`offer-type-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{OFFER_TYPES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Status</Label>
                      <Select value={o.status} onValueChange={(v) => saveNow({ ...doc, offers: doc.offers.map((x) => x.id === o.id ? { ...x, status: v } : x) })}>
                        <SelectTrigger className="rounded-xl" data-testid={`offer-status-${o.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{OFFER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 sm:col-span-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Price ($)</Label>
                      <Input type="number" step="0.01" min="0" data-testid={`offer-price-${o.id}`} value={o.price}
                        onChange={(e) => updateOffer(o.id, "price", e.target.value)} onBlur={save} className="rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Sales this month</Label>
                      <Input type="number" step="1" min="0" data-testid={`offer-sales-${o.id}`} value={o.sales_count}
                        onChange={(e) => updateOffer(o.id, "sales_count", e.target.value)} onBlur={save} className="rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Revenue this month ($)</Label>
                      <Input type="number" step="0.01" min="0" data-testid={`offer-revenue-${o.id}`} value={o.revenue}
                        onChange={(e) => updateOffer(o.id, "revenue", e.target.value)} onBlur={save} className="rounded-xl" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className={`text-xs px-2.5 py-1 rounded-full ${STATUS_TONE[o.status] || "bg-muted"}`}>{o.status}</span>
                  <button data-testid={`offer-remove-${o.id}`} onClick={() => removeOffer(o.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Expenses */}
      <SectionCard
        icon={Receipt}
        title="Expenses"
        hint="Where the money goes. Mark recurring costs and turn items off when they end."
        testid="section-expenses"
        action={<Button size="sm" variant="outline" className="rounded-full" data-testid="add-expense-btn" onClick={addExpense}><Plus className="h-4 w-4 mr-1" /> Add</Button>}
      >
        {(doc.expense_items || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="expenses-empty">No expenses recorded yet.</p>
        ) : (
          <div className="space-y-3" data-testid="expenses-list">
            {doc.expense_items.map((e) => (
              <div key={e.id} className="rounded-2xl border border-border p-4 bg-muted/20" data-testid={`expense-${e.id}`}>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Name</Label>
                    <Input data-testid={`expense-name-${e.id}`} value={e.name} placeholder="Expense name"
                      onChange={(ev) => updateExpense(e.id, "name", ev.target.value)} onBlur={save} className="rounded-xl" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Amount ($)</Label>
                      <Input type="number" step="0.01" min="0" data-testid={`expense-amount-${e.id}`} value={e.amount}
                        onChange={(ev) => updateExpense(e.id, "amount", ev.target.value)} onBlur={save} className="rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Category</Label>
                      <Input data-testid={`expense-category-${e.id}`} value={e.category} placeholder="e.g. Software"
                        onChange={(ev) => updateExpense(e.id, "category", ev.target.value)} onBlur={save} className="rounded-xl" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:col-span-2 items-end">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Recurrence</Label>
                      <Select value={e.recurrence} onValueChange={(v) => saveNow({ ...doc, expense_items: doc.expense_items.map((x) => x.id === e.id ? { ...x, recurrence: v } : x) })}>
                        <SelectTrigger className="rounded-xl" data-testid={`expense-recurrence-${e.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{EXPENSE_RECURRENCE.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 h-10">
                      <Switch checked={!!e.active} data-testid={`expense-active-${e.id}`}
                        onCheckedChange={(v) => saveNow({ ...doc, expense_items: doc.expense_items.map((x) => x.id === e.id ? { ...x, active: v } : x) })} />
                      <span className="text-sm text-muted-foreground">{e.active ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <button data-testid={`expense-remove-${e.id}`} onClick={() => removeExpense(e.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Leads & Customers */}
      <SectionCard icon={Users} title="Leads & Customers" hint="A simple pulse on new interest and new customers this month." testid="section-leads">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label>New leads this month</Label>
            <Input type="number" step="1" min="0" data-testid="new-leads-input" value={doc.new_leads}
              onChange={(e) => setField("new_leads", e.target.value)} onBlur={save} className="rounded-xl" />
          </div>
          <div className="space-y-2">
            <Label>New customers this month</Label>
            <Input type="number" step="1" min="0" data-testid="new-customers-input" value={doc.new_customers}
              onChange={(e) => setField("new_customers", e.target.value)} onBlur={save} className="rounded-xl" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Activity note <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea data-testid="activity-note-input" value={doc.activity_note}
              onChange={(e) => setField("activity_note", e.target.value)} onBlur={save}
              placeholder="Anything worth remembering about leads or customers this month…" className="rounded-xl min-h-[90px]" />
          </div>
        </div>
      </SectionCard>

      {/* Monthly Priorities */}
      <SectionCard icon={Target} title="Monthly Priorities" hint="Up to five things that matter most for the business this month." testid="section-priorities">
        <div className="flex justify-end mb-3">
          <Button variant="outline" size="sm" className="rounded-full" data-testid="copy-priorities-btn" onClick={copyPriorities}>
            <Copy className="h-3.5 w-3.5 mr-2" /> Copy last month's priorities
          </Button>
        </div>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <Input key={i} data-testid={`business-priority-${i}`} value={doc.priorities?.[i] || ""}
              onChange={(e) => setPriority(i, e.target.value)} onBlur={save}
              placeholder={`Priority ${i + 1}`} className="rounded-xl" />
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
